import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { Lock, AlertCircle, Loader2, Check, Eye, EyeOff, ArrowLeft } from "lucide-react";
import PasswordChecklist from "../../../../components/PasswordChecklist";
import "../../LoginScreen.css";
import { registerSchema, type GoogleLinkPasswordInput } from "../../../../lib/validation/schemas/auth.schema";

interface GoogleLinkPasswordStepProps {
  email: string;
  loading: boolean;
  serverError: string;
  onSubmit: (password: string) => void;
  onBack: () => void;
}

export default function GoogleLinkPasswordStep({
  email,
  loading,
  serverError,
  onSubmit,
  onBack,
}: GoogleLinkPasswordStepProps) {
  const { t } = useTranslation(["auth", "common"]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<GoogleLinkPasswordInput>({
    resolver: zodResolver(registerSchema),
    defaultValues: { password: "", confirmPassword: "" },
    mode: "onSubmit",
  });

  const watchedPassword = watch("password", "");

  return (
    <>
      <div className="ls-form-header">
        <button
          type="button"
          onClick={onBack}
          className="ls-header-back-button"
        >
          <ArrowLeft size={16} /> {t("common:actions.back")}
        </button>
        <h2 className="ls-welcome-title">{t("googleLink.passwordTitle")}</h2>
        <p className="ls-welcome-subtitle ls-welcome-subtitle--breakable">{email}</p>
        <p className="ls-form-hint">
          {t("googleLink.passwordHint")}
        </p>
      </div>

      {(errors.password?.message || errors.confirmPassword?.message || serverError) && (
        <div className="ls-error-banner">
          <AlertCircle size={16} className="ls-error-icon" />
          <span>
            {errors.password?.message ?? errors.confirmPassword?.message ?? serverError}
          </span>
        </div>
      )}

      <form
        onSubmit={handleSubmit((data) => onSubmit(data.password))}
        className="ls-form-fields"
      >
        <div className="ls-input-group">
          <label className="ls-input-label">{t("shared.passwordLabel")}</label>
          <div className="auth-input-container ls-input-container">
            <Lock size={18} className="ls-input-icon" />
            <input
              {...register("password")}
              type={showPassword ? "text" : "password"}
              placeholder={t("shared.atLeast8CharsPlaceholder")}
              className="ls-form-input"
              disabled={loading}
              autoFocus
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="ls-eye-toggle"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <div className="ls-input-group">
          <label className="ls-input-label">{t("shared.confirmPasswordLabel")}</label>
          <div className="auth-input-container ls-input-container">
            <Lock size={18} className="ls-input-icon" />
            <input
              {...register("confirmPassword")}
              type={showConfirmPassword ? "text" : "password"}
              placeholder={t("shared.repeatPasswordPlaceholder")}
              className="ls-form-input"
              disabled={loading}
              autoComplete="new-password"
              onPaste={(e) => e.preventDefault()}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword((v) => !v)}
              className="ls-eye-toggle"
            >
              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <PasswordChecklist password={watchedPassword} />

        <button
          type="submit"
          className={`ls-btn-primary${loading ? " ls-btn-primary--disabled" : ""}`}
          disabled={loading}
        >
          {loading ? (
            <Loader2 size={18} className="ls-spinner" />
          ) : (
            <Check size={18} />
          )}
          <span>{loading ? t("googleLink.settingPassword") : t("googleLink.setPasswordAndSignIn")}</span>
        </button>
      </form>
    </>
  );
}
